import { Tooltip } from '@mui/material';
import { sv } from 'date-fns/locale';

import { UserProfileDefaultFields } from 'api';
import { Avatar } from 'components';
import { DEFAULT_DATE_FORMAT } from 'constants/dateFormats';
import ActionsMenu from 'features/employees/components/ActionsMenu';
import { useAuth } from 'hooks';
import i18n from 'i18n';
import {
  EmployeesTableFields,
  RemoveEmployeeArgument,
  User,
  UserProfileField,
  UserRole,
  UserStatus,
} from 'types';
import { formattedDate } from 'utils';

// TODO: refactor adapter
// - move out some components, reuse code
import {
  AdressW,
  Email,
  NameCellW,
  NameW,
  OffboardButton,
  PositionDepartmentText,
  SpanIn,
} from './styled';

interface EmployeeTableAdapterProps {
  data: User[];
  userProfileFields: UserProfileField[];
  removeButtonAction: (arg: RemoveEmployeeArgument) => void;
  activateButtonClick: (id: string) => void;
  reInviteButtonClick: (id: string) => void;
  isManager: boolean;
  isMobile: boolean;
  isLoadingUser: boolean;
  isSwedish: boolean;
}

export function employeeTableAdapter({
  data,
  userProfileFields,
  removeButtonAction,
  activateButtonClick,
  reInviteButtonClick,
  isManager,
  isMobile,
  isSwedish,
}: EmployeeTableAdapterProps): EmployeesTableFields[] {
  if (data.length === 0) {
    return [];
  }

  const removeButtonText = i18n.t('employee.table.actions.remove');
  const offboardButtonText = i18n.t('employee.table.actions.offboard');
  const pendingText = i18n.t('employee.table.actions.pending');
  const managerRoleText = i18n.t('inviteEmployee.form.options.manager');
  const inText = i18n.t('employee.table.description.in');
  const locales = isSwedish ? sv : undefined;

  return data.map(
    ({
      firstName,
      lastName,
      id,
      email,
      phone,
      countryCode,
      avatar,
      status,
      role,
      birthDate,
      position,
      department,
      city,
      invitedById,
    }) => {
      const userName = `${firstName || ''} ${lastName || ''}`.trim();
      const { user: currentUser } = useAuth();
      const avatarSize = isMobile ? 'mobTable' : 'l';
      const isManagerRoleRow = role === UserRole.Manager;
      const invitedByCurrentUser = invitedById === currentUser?.id;

      const positionDepartmentText = () => {
        const visibleFields = userProfileFields.filter((field) => !field.hidden);
        const publicVisibleFields = visibleFields.filter((field) => field.category === 'public');

        const isVisible = (label: string) => visibleFields.some((field) => field.label === label);

        const isPublic = (label: string) =>
          publicVisibleFields.some((field) => field.label === label);

        const showPosition = position && isVisible(UserProfileDefaultFields.POSITION);
        const showDepartment = department && isVisible(UserProfileDefaultFields.DEPARTMENT);
        const isPublicPosition = isPublic(UserProfileDefaultFields.POSITION);
        const isPublicDepartment = isPublic(UserProfileDefaultFields.DEPARTMENT);

        const shouldShowBoth = isManager || (isPublicPosition && isPublicDepartment);

        if (showPosition && showDepartment && shouldShowBoth) {
          return (
            <PositionDepartmentText>
              {position} <SpanIn>{inText}</SpanIn> {department}
            </PositionDepartmentText>
          );
        }

        const showOnly =
          (showPosition && (isManager || isPublicPosition) && position) ||
          (showDepartment && (isManager || isPublicDepartment) && department);

        if (showOnly) {
          return <PositionDepartmentText>{showOnly}</PositionDepartmentText>;
        }

        return null;
      };

      if (status === UserStatus.Invited || status === UserStatus.Registered) {
        return {
          name:
            status === UserStatus.Invited ? (
              <NameCellW>
                <NameW>
                  {pendingText}
                  {isMobile && <Email>{email}</Email>}
                </NameW>
              </NameCellW>
            ) : (
              <NameCellW>
                <Tooltip title={userName} placement="top-start">
                  <NameW>{userName}</NameW>
                </Tooltip>
                {isMobile && <Email>{email}</Email>}
                {role === UserRole.Manager ? <span>{managerRoleText}</span> : null}
                {positionDepartmentText()}
              </NameCellW>
            ),
          id,
          email: email || '',
          avatar: <Avatar size={avatarSize} userId={id} filename={avatar?.small || ''} />,
          phone: phone ? `+${countryCode} ${phone}` : '',
          birthDate: birthDate
            ? formattedDate(new Date(birthDate), DEFAULT_DATE_FORMAT, locales)
            : '',
          city: <AdressW>{city || ''}</AdressW>,
          actions:
            isManager || invitedByCurrentUser ? (
              <ActionsMenu
                isRegister={status === UserStatus.Registered}
                isManager={isManager}
                onRemove={() => removeButtonAction({ id, status, userName })}
                onActivate={() => activateButtonClick(id)}
                onReInvite={() => reInviteButtonClick(id)}
              />
            ) : (
              <></>
            ),
          offboarding: (
            <OffboardButton
              variant="outlined"
              color="info"
              sx={{
                maxWidth: 'none',
                background: 'white',
              }}
              onClick={(e) => {
                e.stopPropagation();
                removeButtonAction({ id, status, userName });
              }}
            >
              {removeButtonText}
            </OffboardButton>
          ),
          position: '',
          isClickable: false,
          class: 'not-hovered',
        };
      }

      if (status === UserStatus.Inactive) {
        return {
          name: (
            <NameCellW>
              <Tooltip title={userName} placement="top-start">
                <NameW>{userName}</NameW>
              </Tooltip>
              {isMobile && <Email>{email}</Email>}
              {isManagerRoleRow ? <span>{managerRoleText}</span> : null}
              {positionDepartmentText()}
            </NameCellW>
          ),
          id,
          email: email || '',
          avatar: <Avatar size={avatarSize} userId={id} filename={avatar?.small || ''} />,
          phone: phone ? `+${countryCode} ${phone}` : '',
          birthDate: birthDate
            ? formattedDate(new Date(birthDate), DEFAULT_DATE_FORMAT, locales)
            : '',
          city: <AdressW>{city || ''}</AdressW>,
          actions: <></>,
          position: '',
          isClickable: true,
          class: 'inactive',
          offboarding: (
            <OffboardButton
              variant="contained"
              color="warning"
              sx={{
                maxWidth: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
              }}
            >
              {offboardButtonText}
            </OffboardButton>
          ),
        };
      }

      return {
        name: (
          <NameCellW>
            <Tooltip title={userName} placement="top-start">
              <NameW>{userName}</NameW>
            </Tooltip>
            {isMobile && <Email>{email}</Email>}
            {isManagerRoleRow ? <span>{managerRoleText}</span> : null}
            {positionDepartmentText()}
          </NameCellW>
        ),
        id,
        email: email || '',
        avatar: (
          <Avatar
            size={avatarSize}
            userId={id}
            filename={avatar?.small || ''}
            withNavigation={false}
          />
        ),
        phone: phone ? `+${countryCode} ${phone}` : '',
        birthDate: birthDate
          ? formattedDate(new Date(birthDate), DEFAULT_DATE_FORMAT, locales)
          : '',
        city: <AdressW>{!isMobile && city ? city : ''}</AdressW>,
        actions: <></>,
        isClickable: status === UserStatus.Active || status === UserStatus.Registered,
        offboarding: (
          <>
            {currentUser?.id !== id ? (
              <OffboardButton
                variant="contained"
                color="warning"
                sx={{
                  maxWidth: 'none',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeButtonAction({ id, status, userName });
                }}
              >
                {offboardButtonText}
              </OffboardButton>
            ) : null}
          </>
        ),
      };
    },
  );
}
